import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import {
  ArrowLeft, TrendingUp, TrendingDown, DollarSign, Play,
  RotateCcw, ChevronRight, Zap, Award, Wallet, Bitcoin
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function BitcoinSimulator() {
  const navigate = useNavigate();
  const [simData, setSimData] = useState(null);
  const [currentYearIdx, setCurrentYearIdx] = useState(0);
  const [decisions, setDecisions] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState("intro"); // intro, playing, result
  const [cash, setCash] = useState(10000);
  const [btcHeld, setBtcHeld] = useState(0);

  useEffect(() => {
    api.get("/games-v2/simulator/data").then(res => {
      setSimData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleDecision = (action) => {
    if (!simData) return;
    const year = simData.years[currentYearIdx];
    const pct = action === "hold" ? 0 : action === "dca" ? 20 : 50;
    const newDecision = { year: year.year, action, amount_pct: pct };
    const newDecisions = [...decisions, newDecision];
    setDecisions(newDecisions);

    // Update portfolio in real-time
    const priceStart = year.price_start || 0.001;
    const priceEnd = year.price_end || priceStart;
    let newCash = cash;
    let newBtc = btcHeld;

    if (action === "buy") {
      const spend = newCash * 0.5;
      if (spend > 0 && priceStart > 0) {
        newBtc += spend / priceStart;
        newCash -= spend;
      }
    } else if (action === "sell") {
      const sellBtc = newBtc * 0.5;
      if (sellBtc > 0) {
        newCash += sellBtc * priceEnd;
        newBtc -= sellBtc;
      }
    } else if (action === "dca") {
      const avgPrice = (priceStart + priceEnd) / 2;
      const spend = newCash * 0.2;
      if (spend > 0 && avgPrice > 0) {
        newBtc += spend / avgPrice;
        newCash -= spend;
      }
    }

    setCash(newCash);
    setBtcHeld(newBtc);

    if (currentYearIdx < simData.years.length - 1) {
      setCurrentYearIdx(currentYearIdx + 1);
    } else {
      submitGame(newDecisions);
    }
  };

  const submitGame = async (allDecisions) => {
    setSubmitting(true);
    try {
      const res = await api.post("/games-v2/simulator/play", allDecisions);
      setResult(res.data);
      setPhase("result");
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const resetGame = () => {
    setDecisions([]);
    setCurrentYearIdx(0);
    setResult(null);
    setPhase("intro");
    setCash(10000);
    setBtcHeld(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentYear = simData?.years[currentYearIdx];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="simulator-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">BITCOIN SIMULATOR</h1>
          <span className="badge-orange font-mono">{simData?.years.length} years</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-center space-y-6 py-12" data-testid="simulator-intro"
            >
              <div className="w-20 h-20 mx-auto rounded-full bg-[#F7931A]/20 flex items-center justify-center">
                <DollarSign className="text-[#F7931A]" size={40} />
              </div>
              <h2 className="font-heading text-3xl font-bold text-white">TIME MACHINE</h2>
              <p className="text-zinc-400 max-w-md mx-auto">
                It's 2009. You have <span className="text-[#F7931A] font-mono">$10,000</span>.
                Bitcoin just launched. Make decisions each year and see how your strategy plays out.
              </p>
              <Button onClick={() => setPhase("playing")} className="btn-primary" data-testid="start-simulator-btn">
                <Play size={18} className="mr-2" /> Start Simulation
              </Button>
            </motion.div>
          )}

          {phase === "playing" && currentYear && (
            <motion.div key="playing" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}
              className="space-y-6" data-testid="simulator-playing"
            >
              {/* Year Header */}
              <div className="card-dark p-6 text-center">
                <p className="text-zinc-500 text-sm">Year {currentYearIdx + 1} of {simData.years.length}</p>
                <h2 className="font-heading text-5xl font-bold text-[#F7931A] my-2">{currentYear.year}</h2>
                <p className="text-white">{currentYear.event}</p>
              </div>

              {/* Live Portfolio */}
              <div className="grid grid-cols-3 gap-2" data-testid="portfolio-display">
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center border border-white/5">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <Wallet className="text-green-400" size={14} />
                    <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Cash</span>
                  </div>
                  <p className="font-mono text-lg font-bold text-green-400" data-testid="portfolio-cash">
                    ${cash < 1000 ? cash.toFixed(2) : Math.round(cash).toLocaleString()}
                  </p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center border border-white/5">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <Bitcoin className="text-[#F7931A]" size={14} />
                    <span className="text-zinc-500 text-[10px] uppercase tracking-wider">BTC</span>
                  </div>
                  <p className="font-mono text-lg font-bold text-[#F7931A]" data-testid="portfolio-btc">
                    {btcHeld < 0.01 ? btcHeld.toFixed(4) : btcHeld < 1 ? btcHeld.toFixed(3) : btcHeld.toFixed(2)}
                  </p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center border border-[#F7931A]/20">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <DollarSign className="text-white" size={14} />
                    <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Total</span>
                  </div>
                  <p className="font-mono text-lg font-bold text-white" data-testid="portfolio-total">
                    ${(() => {
                      const priceEnd = currentYear.price_end || currentYear.price_start || 0;
                      const total = cash + (btcHeld * priceEnd);
                      return total < 1000 ? total.toFixed(2) : Math.round(total).toLocaleString();
                    })()}
                  </p>
                </div>
              </div>

              {/* Price Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="card-dark p-4 text-center">
                  <p className="text-zinc-500 text-xs">BTC Price Start</p>
                  <p className="font-mono text-xl text-white">${currentYear.price_start?.toLocaleString() || "???"}</p>
                </div>
                <div className="card-dark p-4 text-center">
                  <p className="text-zinc-500 text-xs">BTC Price End</p>
                  <p className="font-mono text-xl text-white">
                    {currentYear.price_end ? `$${currentYear.price_end.toLocaleString()}` : "???"}
                  </p>
                </div>
              </div>

              {/* Decision Progress */}
              <div className="flex gap-1">
                {simData.years.map((_, i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full ${
                    i < currentYearIdx ? "bg-[#F7931A]" : i === currentYearIdx ? "bg-[#F7931A]/50 animate-pulse" : "bg-zinc-800"
                  }`} />
                ))}
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { action: "buy", label: "BUY 50%", desc: "Invest half your cash", icon: TrendingUp, color: "border-green-500/50 hover:bg-green-500/10" },
                  { action: "sell", label: "SELL 50%", desc: "Sell half your BTC", icon: TrendingDown, color: "border-red-500/50 hover:bg-red-500/10" },
                  { action: "dca", label: "DCA 20%", desc: "Dollar cost average", icon: DollarSign, color: "border-[#F7931A]/50 hover:bg-[#F7931A]/10" },
                  { action: "hold", label: "HOLD", desc: "Do nothing", icon: Award, color: "border-[#00E5FF]/50 hover:bg-[#00E5FF]/10" },
                ].map(({ action, label, desc, icon: Icon, color }) => (
                  <button
                    key={action}
                    onClick={() => handleDecision(action)}
                    disabled={submitting}
                    className={`card-dark p-4 text-center ${color} transition-all`}
                    data-testid={`sim-action-${action}`}
                  >
                    <Icon className="mx-auto mb-2 text-white" size={24} />
                    <p className="text-white font-heading text-lg font-bold">{label}</p>
                    <p className="text-zinc-500 text-xs">{desc}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {phase === "result" && result && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="space-y-6" data-testid="simulator-result"
            >
              <div className="card-dark p-8 text-center">
                <h2 className="font-heading text-3xl font-bold text-white mb-2">SIMULATION COMPLETE</h2>
                <p className="font-mono text-5xl font-bold gradient-text my-4">
                  ${result.final_value?.toLocaleString()}
                </p>
                <p className="text-zinc-400">
                  You turned $10,000 into ${result.final_value?.toLocaleString()}
                </p>
                <div className="flex justify-center gap-6 mt-4">
                  <div>
                    <p className="text-zinc-500 text-xs">BTC Held</p>
                    <p className="font-mono text-white">{result.btc_held}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">Cash Left</p>
                    <p className="font-mono text-white">${result.cash_remaining?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">Score</p>
                    <p className="font-mono text-[#F7931A]">{result.score}</p>
                  </div>
                </div>
              </div>

              {result.sat_reward > 0 && (
                <div className="card-dark p-4 flex items-center justify-center gap-3 border-[#F7931A]/30">
                  <Zap className="text-[#F7931A]" size={20} />
                  <span className="text-white">Earned <span className="font-mono text-[#F7931A]">{result.sat_reward} BTC XP</span></span>
                </div>
              )}

              <div className="card-dark p-6">
                <p className="text-zinc-400 text-sm italic">{result.lesson}</p>
              </div>

              {/* Timeline */}
              <div className="card-dark p-6">
                <h3 className="text-white font-medium mb-4">Your Timeline</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {result.history?.map((h) => (
                    <div key={h.year} className="flex items-center justify-between text-sm py-1 border-b border-white/5">
                      <span className="font-mono text-zinc-400">{h.year}</span>
                      <span className={`uppercase text-xs font-bold ${
                        h.action === "buy" ? "text-green-400" :
                        h.action === "sell" ? "text-red-400" :
                        h.action === "dca" ? "text-[#F7931A]" : "text-[#00E5FF]"
                      }`}>{h.action}</span>
                      <span className="font-mono text-white">${h.portfolio_value?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={resetGame} className="btn-secondary flex-1" data-testid="restart-simulator-btn">
                  <RotateCcw size={18} className="mr-2" /> Play Again
                </Button>
                <Button onClick={() => navigate("/dashboard")} className="btn-primary flex-1">
                  Dashboard <ChevronRight size={18} />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
